import { ENV } from "../../../config";
import { SESSION_CONSTANT } from "../../../shared/utils/SessionConstants";
import { loadSsoFeatures, SsoLoadResult } from "./SsoFeatureService";

// "no-session" means there is no signed-in SSO account to refresh for (a username/password login).
export type SsoRefreshResult = SsoLoadResult | "no-session";

// Re-runs the SSO session registration and feature load that index.tsx does once after an SSO login,
// for the user to trigger from the UI when the stored permissions are empty or were never written.
// msalInstance is imported on demand so that code which only needs the result type does not
// construct an MSAL client at import time.
const runSsoFeatureLoad = async (): Promise<SsoRefreshResult> => {
  const { msalInstance, API_SCOPE } = await import("../../../core/services/msalInstance");
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return "no-session";
  }

  let accessToken: string;
  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account });
    accessToken = tokenResponse.accessToken;
    localStorage.setItem(SESSION_CONSTANT.TOKEN_KEY, accessToken);
  } catch (tokenError) {
    console.warn("[MSAL] Silent token acquisition failed during permission refresh:", tokenError);
    return "failed";
  }

  try {
    const response = await fetch(`${ENV.API_URL}/api/auth/sso-session`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    // The BackEnd answers 401 "User not found" when no Username row matches the token's
    // preferred_username: the same "account not matched" case as null ids.
    if (response.status === 401) {
      return "unmatched";
    }
    if (!response.ok) {
      return "failed";
    }
    return await loadSsoFeatures(response);
  } catch (sessionError) {
    console.warn("[MSAL] SSO session call failed during permission refresh:", sessionError);
    return "failed";
  }
};

export const refreshSsoFeatures = async (): Promise<SsoRefreshResult> => runSsoFeatureLoad();

// True when localStorage["features"] holds at least one feature key. Missing, unreadable or [] is "no".
const hasStoredFeatures = (): boolean => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SESSION_CONSTANT.FEATURES_KEY) ?? "[]");
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
};

// Result of the one startup attempt, so the Inputs tab can explain an unmatched account without
// a click. Null until the attempt has run and whenever it did nothing.
let startupResult: SsoRefreshResult | null = null;
let startupAttempted = false;
export const getStartupSsoResult = (): SsoRefreshResult | null => startupResult;

// App start with an MSAL account restored from the cache (no redirect result): index.tsx's load only
// runs after a redirect, so a cached session would otherwise never get its features. Runs at most once
// per page load (no retry on a 401 or any other failure), never when a good value is stored, and the
// load itself replaces the stored value only on a successful fetch.
export const restoreCachedSsoFeatures = async (): Promise<SsoRefreshResult | null> => {
  if (startupAttempted || hasStoredFeatures()) {
    return null;
  }
  startupAttempted = true;
  startupResult = await runSsoFeatureLoad();
  return startupResult;
};

// Test seam: lets a suite re-run the once-per-page-load attempt.
export const resetStartupSsoRestoreForTests = (): void => {
  startupAttempted = false;
  startupResult = null;
};
